import { enums, enumsTypes } from "@shared/constants";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import Tooltip from "@shared/components/ui-kit/overlays/Tooltip/Tooltip.web";
import { CUSTOMER_REVIEWS } from "@shared/constants/event-detail";
import { RecommendedRating } from "@shared/modules/ratings/types/ratings";

export type EventStatusBadgeProps = {
  eventStatus?:
    | enumsTypes.EventStatusIdType
    | enumsTypes.MembershipGroupStatusMapIdType;
  soldOut?: boolean;
  waitlisted?: boolean;
  recurringData?: {
    isRecurring: boolean;
    isMultiDay: boolean;
  };
  recommendedRating: RecommendedRating;
};

const EventStatusBadge: React.FC<EventStatusBadgeProps> = ({
  eventStatus,
  soldOut,
  waitlisted,
  recurringData,
  recommendedRating,
}) => {
  const { t } = useTranslation("constants");

  if (eventStatus === enums.EVENT_STATUS_MAP.CANCELLED.id) {
    return (
      <Box
        borderRadius="sm"
        display="inline-flex"
        overflow="hidden"
        backgroundColor="red.200"
        border="1px solid"
        borderColor="red.800"
        color="red.800"
        padding={{ base: "4px 16px", sm: "4px 8px" }}
      >
        <Text
          fontSize={{ base: "xs", sm: "2xs" }}
          lineHeight={{ base: "16px", sm: "12px" }}
        >
          {t(enums.EVENT_STATUS_MAP.CANCELLED.name)}
        </Text>
      </Box>
    );
  }

  if (eventStatus === enums.EVENT_STATUS_MAP.POSTPONED.id) {
    return (
      <Box
        borderRadius="sm"
        display="inline-flex"
        overflow="hidden"
        backgroundColor="yellow.200"
        border="1px solid"
        borderColor="yellow.800"
        color="yellow.800"
        padding={{ base: "4px 16px", sm: "4px 8px" }}
      >
        <Text
          fontSize={{ base: "xs", sm: "2xs" }}
          lineHeight={{ base: "16px", sm: "12px" }}
        >
          {t(enums.EVENT_STATUS_MAP.POSTPONED.name)}
        </Text>
      </Box>
    );
  }

  if (soldOut) {
    if (waitlisted) {
      return (
        <Box
          borderRadius="sm"
          display="inline-flex"
          overflow="hidden"
          backgroundColor="primary.100"
          border="1px solid"
          borderColor="primary.600"
          color="primary.600"
          padding={{ base: "4px 16px", sm: "4px 8px" }}
        >
          <Text
            fontSize={{ base: "xs", sm: "2xs" }}
            lineHeight={{ base: "16px", sm: "12px" }}
          >
            {t("Waitlist", { ns: "common" })}
          </Text>
        </Box>
      );
    }

    return (
      <Box
        borderRadius="sm"
        display="inline-flex"
        overflow="hidden"
        backgroundColor="secondary.200"
        border="1px solid"
        borderColor="secondary.800"
        color="secondary.800"
        padding={{ base: "4px 16px", sm: "4px 8px" }}
      >
        <Text
          fontSize={{ base: "xs", sm: "2xs" }}
          lineHeight={{ base: "16px", sm: "12px" }}
        >
          {t("Sold Out", { ns: "common" })}
        </Text>
      </Box>
    );
  }

  if (recurringData && recurringData.isRecurring) {
    return (
      <Box
        borderRadius="sm"
        display="inline-flex"
        overflow="hidden"
        backgroundColor="indigo.200"
        border="1px solid"
        borderColor="indigo.800"
        color="indigo.800"
        padding={{ base: "4px 16px", sm: "4px 8px" }}
      >
        <Text
          fontSize={{ base: "xs", sm: "2xs" }}
          lineHeight={{ base: "16px", sm: "12px" }}
        >
          {recurringData.isMultiDay
            ? t("Multiple Dates", { ns: "common" })
            : t("Multiple Times", { ns: "common" })}
        </Text>
      </Box>
    );
  }

  if (recommendedRating.isRecommended) {
    return (
      <Tooltip
        label={t(CUSTOMER_REVIEWS.USER_RATING_FOUR_OR_MORE, {
          percentage: recommendedRating.recommendedPercentage,
          ns: "common",
        })}
        hasArrow
      >
        <Box
          borderRadius="sm"
          display="inline-flex"
          overflow="hidden"
          backgroundColor="pink.100"
          border="1px solid"
          borderColor="pink.600"
          color="pink.600"
          padding={{ base: "4px 16px", sm: "4px 8px" }}
        >
          <Text
            fontSize={{ base: "xs", sm: "2xs" }}
            lineHeight={{ base: "16px", sm: "12px" }}
          >
            {t(CUSTOMER_REVIEWS.RECOMMENDED_BY_FANS, {
              percentage: recommendedRating.recommendedPercentage,
              ns: "common",
            })}
          </Text>
        </Box>
      </Tooltip>
    );
  }

  return <div />;
};

export default EventStatusBadge;
